<script setup lang="ts">
import { computed, ref } from "vue";
import { bemBuilder } from "@chatfood/core-utils";
import {
  AtomText,
  SatTooltip,
  SatIconButton,
  AtomLoadingSpinner,
} from "@chatfood/design-system";
import { useLocale } from "~/composables/use-locale";
import { useBasketStore } from "~/stores/basket";
import { useModalHubStore } from "~/components/modal-hub";
import { useBugReporter } from "#imports";
import { I18nTranslate } from "~/components/i18n-translate";

const css = bemBuilder("discount-view");

const { t } = useLocale();
const { report } = useBugReporter();

const basket = useBasketStore();
const modalHub = useModalHubStore();

const isRemovingDiscount = ref(false);

const onRemoveCode = async () => {
  isRemovingDiscount.value = true;

  try {
    await basket.removeDiscount();
  } catch (e) {
    report(e);
  } finally {
    isRemovingDiscount.value = false;
  }
};

const discountCode = computed(() => basket.discount?.code);

const disabledMessageTooltip = computed(() => {
  if (basket.loyalties.length) {
    return t("components.discount_view.discount_cta_tooltip");
  }

  return "";
});

const colorText = computed(() =>
  disabledMessageTooltip.value ? "color-text-disabled" : "color-primary",
);

const showDiscounts = () => {
  if (disabledMessageTooltip.value) return;

  modalHub.showDiscounts();
};
</script>

<template>
  <div :class="css()">
    <SatTooltip :content="disabledMessageTooltip">
      <div
        data-test="box"
        :class="[
          css('box'),
          { [css('box', 'disabled')]: Boolean(disabledMessageTooltip) },
        ]"
      >
        <a
          v-if="!basket.discount"
          href=""
          data-test="cta-open-selection"
          :class="css('open-selection')"
          @click.prevent="showDiscounts"
        >
          <AtomText type="body-bold" :color="colorText">
            {{ t("components.discount_view.cta_open_selection_label") }}
          </AtomText>
        </a>
        <div v-else :class="css('filled')">
          <I18nTranslate
            keypath="components.discount_view.applied_label"
            tag="div"
            data-test="discount-code"
          >
            <template #discountCode>
              <strong>{{ discountCode }}</strong>
            </template>
          </I18nTranslate>
          <AtomLoadingSpinner v-if="isRemovingDiscount" :size="21" />
          <SatIconButton
            v-else
            variant="danger"
            theme="ghost"
            icon-name="delete"
            size="extra-small"
            @click="onRemoveCode"
          />
        </div>
      </div>
    </SatTooltip>
  </div>
</template>

<style lang="scss" scoped>
.discount-view {
  &__box {
    width: 100%;
    min-height: 51px;
    border-radius: var(--radii-card-sm);
    border: 1px solid var(--color-base-90);
    background: var(--color-base-100);

    &--disabled {
      pointer-events: none;
      cursor: no-drop;
      background: var(--color-base-95);
    }
  }

  &__open-selection {
    display: block;
    padding: var(--spacing-md);
  }

  &__filled {
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: var(--spacing-sm);
    padding: var(--spacing-md);
  }
}
</style>
